import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, ChevronRight, ShieldCheck, Building2, ArrowLeft, AlertCircle } from 'lucide-react';
import AdinkraPattern from '@/components/layout/AdinkraPattern';
import { useAuth, formatApiError } from '@/lib/auth';
import { moduleBySlug } from '@/lib/modules';

export default function Login() {
  const [show, setShow] = useState(false);
  const location = useLocation();
  // ?type=admin → Platform admin tab default; ?type=user → Organization tab (regular user / manager)
  const initialType = new URLSearchParams(location.search).get('type');
  const [tab, setTab] = useState(initialType === 'admin' ? 'admin' : 'org');
  const [orgId, setOrgId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const u = await login({
        email,
        password,
        org_id: tab === 'org' ? orgId.trim().toLowerCase() : undefined,
      });
      // Role-aware destination:
      //   super_admin → /control-panel
      //   team_member → /my-workspace (self-service)
      //   security_officer → /dashboard/hr (will open on Visitors tab; security tools only)
      //   org_manager / staff / client → first activated module
      const firstValid = (u.modules || []).find((s) => moduleBySlug(s));
      let dest;
      if (location.state?.from?.pathname) {
        dest = location.state.from.pathname;
      } else if (u.role === 'super_admin') {
        dest = '/control-panel';
      } else if (u.role === 'team_member') {
        dest = '/my-workspace';
      } else if (u.role === 'security_officer') {
        dest = '/dashboard/hr';
      } else {
        dest = firstValid ? `/dashboard/${firstValid}` : '/team';
      }
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex relative bg-navy text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]"><AdinkraPattern color="#F59B2D" /></div>
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-amber/20 blur-[120px]" />
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-amber transition mb-12">
            <ArrowLeft className="w-4 h-4" /> Back to platform
          </Link>
          <div className="flex items-center gap-3">
            <img src="/logos/attp-main.png" alt="" className="h-12 w-12 object-contain" />
            <div>
              <div className="font-display text-2xl font-bold">ATTP <span className="text-amber">NEXUS</span></div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/50">Run Africa, smarter.</div>
            </div>
          </div>
        </div>
        <div className="relative">
          <h2 className="font-display font-bold text-4xl tracking-tight leading-[1.1] text-balance">
            One sign-in. Ten modules.<br /><span className="text-amber">Zero noise.</span>
          </h2>
          <p className="mt-5 text-white/70 max-w-md leading-relaxed">
            Domain-isolated authentication. Role-aware. Multi-tenant by design.
          </p>

          <div className="mt-8 bg-white/5 border border-white/10 rounded-md p-4 text-sm">
            <div className="label-tag text-amber mb-2">Secure workspace access</div>
            <p className="text-white/70 text-xs leading-relaxed">
              Sign in with your organisation credentials. If you have forgotten your
              password or need workspace access, contact your organisation administrator.
            </p>
          </div>
        </div>
        <div className="relative text-xs text-white/40">© {new Date().getFullYear()} ATTP Nexus</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-neutral-50">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-navy mb-8">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="label-tag text-amber-600 mb-3">Sign in</div>
          <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-navy mb-2">Welcome back.</h1>
          <p className="text-neutral-600 mb-8">Sign in to continue to your workspace.</p>

          <div className="grid grid-cols-2 gap-1 p-1 bg-white border border-neutral-200 rounded-md mb-7" data-testid="auth-tabs">
            <button onClick={() => setTab('org')} className={`py-2.5 rounded text-sm font-medium transition flex items-center justify-center gap-2 ${tab === 'org' ? 'bg-navy text-white' : 'text-neutral-600 hover:text-navy'}`} data-testid="tab-organization">
              <Building2 className="w-4 h-4" /> Organization
            </button>
            <button onClick={() => setTab('admin')} className={`py-2.5 rounded text-sm font-medium transition flex items-center justify-center gap-2 ${tab === 'admin' ? 'bg-navy text-white' : 'text-neutral-600 hover:text-navy'}`} data-testid="tab-admin">
              <ShieldCheck className="w-4 h-4" /> Platform admin
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            {tab === 'org' && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-600 mb-2">Organization ID</label>
                <input value={orgId} onChange={(e) => setOrgId(e.target.value)} type="text" placeholder="lagos-mainland-hospital" className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-md focus:border-navy focus:ring-2 focus:ring-navy/10 outline-none transition" data-testid="input-org-id" required />
                <p className="text-[11px] text-neutral-500 mt-1.5">Your unique workspace identifier.</p>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-600 mb-2">Email · Username · Employee # · Phone</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="text" placeholder="you@organization.africa  ·  000001  ·  +234803…" className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-md focus:border-navy focus:ring-2 focus:ring-navy/10 outline-none transition" data-testid="input-email" required autoComplete="username" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-600 mb-2">Password</label>
              <div className="relative">
                <input value={password} onChange={(e) => setPassword(e.target.value)} type={show ? 'text' : 'password'} placeholder="••••••••••••" className="w-full px-4 py-3 pr-11 bg-white border border-neutral-200 rounded-md focus:border-navy focus:ring-2 focus:ring-navy/10 outline-none transition" data-testid="input-password" required autoComplete="current-password" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-navy" data-testid="toggle-password">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-rose-50 border border-rose-200 rounded-md text-sm text-rose-700" data-testid="login-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <button type="submit" disabled={busy} className="btn-primary w-full justify-center text-base mt-2 disabled:opacity-60" data-testid="submit-login">
              {busy ? 'Signing in…' : (<>Continue <ChevronRight className="w-4 h-4" /></>)}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-neutral-200 text-sm text-center text-neutral-600">
            Need a workspace? <Link to="/" className="text-navy font-medium hover:text-amber-600">Talk to us</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
